import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, KeyRound, ShieldCheck, User as UserIcon } from "lucide-react";

const formatDetail = (detail, fallback) => {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

export default function UserManagementSection() {
  const { user: me } = useAuth();
  const { t } = useLang();
  const qc = useQueryClient();

  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: me?.role === "admin",
    retry: false,
  });

  if (me?.role !== "admin") {
    return (
      <div className="text-sm text-muted-foreground font-mono">
        {t("users_admin_only")}
      </div>
    );
  }

  if (isLoading) {
    return <div className="text-sm text-muted-foreground font-mono">Loading…</div>;
  }

  if (error) {
    return (
      <div className="text-sm text-destructive font-mono">
        {formatDetail(error?.response?.data?.detail, t("users_load_error"))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-muted-foreground font-mono">
          {t("users_description")}
        </p>
        <CreateUserDialog />
      </div>

      <ul className="space-y-2" data-testid="users-list">
        {users.map((u) => (
          <li
            key={u.user_id}
            data-testid={`user-row-${u.user_id}`}
            className="flex items-center justify-between border border-border rounded-md px-4 py-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              {u.role === "admin" ? (
                <ShieldCheck className="h-4 w-4 text-[hsl(var(--chart-3))] shrink-0" />
              ) : (
                <UserIcon className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{u.name || u.email}</div>
                <div className="text-xs font-mono text-muted-foreground truncate">
                  {u.email}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge
                variant={u.role === "admin" ? "default" : "secondary"}
                className="font-mono text-[10px] uppercase tracking-wider"
              >
                {u.role}
              </Badge>
              <ChangePasswordDialog user={u} />
              {u.user_id !== me?.user_id && <DeleteUserDialog user={u} qc={qc} />}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CreateUserDialog() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "user" });
  const [err, setErr] = useState("");

  const create = useMutation({
    mutationFn: async () => (await api.post("/users", form)).data,
    onSuccess: () => {
      toast.success(t("users_created_toast"));
      qc.invalidateQueries({ queryKey: ["users"] });
      setForm({ email: "", name: "", password: "", role: "user" });
      setErr("");
      setOpen(false);
    },
    onError: (e) => {
      setErr(formatDetail(e?.response?.data?.detail, t("users_create_error")));
    },
  });

  const submit = (e) => {
    e.preventDefault();
    setErr("");
    if (!form.email || !form.password) {
      setErr(t("users_missing_fields"));
      return;
    }
    if (form.password.length < 8) {
      setErr(t("users_password_too_short"));
      return;
    }
    create.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5" data-testid="add-user-button">
          <UserPlus className="h-3.5 w-3.5" />
          {t("users_add")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("users_add")}</DialogTitle>
          <DialogDescription>{t("users_add_desc")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="u-email">{t("login_email_label")}</Label>
            <Input
              id="u-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="user@company.com"
              data-testid="add-user-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="u-name">{t("users_name")}</Label>
            <Input
              id="u-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Doe"
              data-testid="add-user-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="u-pass">{t("login_password_label")}</Label>
            <Input
              id="u-pass"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={t("users_password_hint")}
              data-testid="add-user-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("users_role")}</Label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v })}
            >
              <SelectTrigger data-testid="add-user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {err && <div className="text-sm text-destructive">{err}</div>}
          <DialogFooter>
            <Button
              type="submit"
              disabled={create.isPending}
              data-testid="add-user-submit-button"
            >
              {create.isPending ? "…" : t("users_create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ChangePasswordDialog({ user }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");

  const mut = useMutation({
    mutationFn: async () =>
      (await api.put(`/users/${user.user_id}/password`, { password: pw })).data,
    onSuccess: () => {
      toast.success(t("users_pw_changed_toast"));
      setPw("");
      setErr("");
      setOpen(false);
    },
    onError: (e) => {
      setErr(formatDetail(e?.response?.data?.detail, t("users_pw_error")));
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid={`change-password-btn-${user.user_id}`}
          className="gap-1.5"
        >
          <KeyRound className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("users_change_password")}</DialogTitle>
          <DialogDescription className="font-mono text-xs">{user.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder={t("users_new_password")}
            data-testid={`change-password-input-${user.user_id}`}
          />
          {err && <div className="text-sm text-destructive">{err}</div>}
          <Button
            onClick={() => {
              setErr("");
              if (pw.length < 8) {
                setErr(t("users_password_too_short"));
                return;
              }
              mut.mutate();
            }}
            disabled={mut.isPending}
            className="w-full"
            data-testid={`change-password-submit-${user.user_id}`}
          >
            {mut.isPending ? "…" : t("users_save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteUserDialog({ user, qc }) {
  const { t } = useLang();
  const del = useMutation({
    mutationFn: async () => (await api.delete(`/users/${user.user_id}`)).data,
    onSuccess: () => {
      toast.success(t("users_deleted_toast"));
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => {
      toast.error(formatDetail(e?.response?.data?.detail, t("users_delete_error")));
    },
  });

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-destructive hover:text-destructive"
          data-testid={`delete-user-btn-${user.user_id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("users_delete_title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("users_delete_desc").replace("{email}", user.email)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => del.mutate()}
            data-testid={`delete-user-confirm-${user.user_id}`}
          >
            {t("users_delete_confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
